/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

export default function useProductPreview(onTotalChange) {
  const { watch } = useFormContext() || {};
  const formItems = watch ? watch('items') : null;

  const [quantities, setQuantities] = useState({});
  const [selectedChoices, setSelectedChoices] = useState({});
  const [subChoiceQuantities, setSubChoiceQuantities] = useState({});

  const rawItems = formItems || [];
  const displayItems = [];

  rawItems.forEach((item, idx) => {
    const photoUrl = typeof item.image === 'string' ? item.image : item.image?.url || item.photoUrl || null;
    const subGroups = [];

    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subGroup) => {
        const groupChoices = [];
        if (subGroup.list && subGroup.list.length > 0) {
          subGroup.list.forEach((choice, cIdx) => {
            const name = typeof choice === 'string' ? choice : choice?.name;
            const price = Number(typeof choice === 'object' && choice?.price ? choice.price : 0);
            if (name) {
              groupChoices.push({
                index: cIdx,
                name,
                price,
              });
            }
          });
        }
        if (groupChoices.length > 0 || subGroup.required) {
          subGroups.push({
            type: subGroup.type || '',
            selectionMode: subGroup.selectionMode || 'single',
            choices: groupChoices,
            isRequired: Boolean(subGroup.required === 1 || subGroup.required === true),
          });
        }
      });
    } else if (item.options && item.options.length > 0) {
      subGroups.push({
        type: '',
        selectionMode: 'single',
        choices: item.options.map((opt, oIdx) => ({
          index: oIdx,
          name: typeof opt === 'string' ? opt : opt?.name || '',
          price: Number(typeof opt === 'object' && opt?.price ? opt.price : 0),
        })),
        isRequired: Boolean(item.isRequired),
      });
    }

    const hasSplitCase = Boolean(
      item.splitCasePurchase &&
      (Number(item.splitCasePurchase.itemsPerCase) > 0 || Number(item.splitCasePurchase.costPerItem) > 0)
    );

    if (hasSplitCase) {
      const itemsPerCase = Number(item.splitCasePurchase.itemsPerCase || 0);
      const costPerItem = Number(item.splitCasePurchase.costPerItem || 0);
      const mainPrice = item.price !== undefined && item.price !== '' ? Number(item.price) : 0;
      const splitName = item.splitCasePurchase.name || item.name || `Item ${idx + 1}`;
      const splitPhotoUrl = item.splitCasePurchase.photoUrl || photoUrl;

      displayItems.push({
        id: `${item.id || idx + 1}-case`,
        name: item.name || `Item ${idx + 1}`,
        basePrice: mainPrice,
        description: itemsPerCase ? `${itemsPerCase} per case/carton` : (item.description || ''),
        photoUrl,
        subGroups,
        ordersMade: item.ordersMade !== undefined ? item.ordersMade : (item.id === 2 ? 1 : 0),
        isPackageIcon: !photoUrl,
        isSplitCaseItem: false,
      });

      displayItems.push({
        id: `${item.id || idx + 1}-split`,
        name: splitName,
        basePrice: costPerItem,
        description: 'Individual pieces (split case)',
        photoUrl: splitPhotoUrl,
        subGroups: [],
        ordersMade: item.splitCaseOrdersMade !== undefined ? item.splitCaseOrdersMade : 0,
        isPackageIcon: !splitPhotoUrl,
        isSplitCaseItem: true,
        splitCaseDetails: {
          itemsPerCase,
          currentFill: 0,
          needMore: itemsPerCase,
        },
      });
    } else {
      const itemPrice = item.price !== undefined && item.price !== '' ? Number(item.price) : 0;
      displayItems.push({
        id: item.id || idx + 1,
        name: item.name || `Item ${idx + 1}`,
        basePrice: itemPrice,
        description: item.description || '',
        photoUrl,
        subGroups,
        ordersMade: item.ordersMade ?? 0,
        isPackageIcon: false,
        isSplitCaseItem: false,
      });
    }
  });

  const handleQuantityChange = (itemId, delta) => {
    setQuantities((prev) => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [itemId]: next };
    });
  };

  const handleSelectChoice = (itemId, groupIdx, choiceIdx, selectionMode) => {
    setSelectedChoices((prev) => {
      const itemGroupSelections = prev[itemId]?.[groupIdx] || [];
      let updatedGroupSelections = [];

      if (selectionMode === 'multiple') {
        if (itemGroupSelections.includes(choiceIdx)) {
          updatedGroupSelections = itemGroupSelections.filter((i) => i !== choiceIdx);
        } else {
          updatedGroupSelections = [...itemGroupSelections, choiceIdx];
        }
      } else if (itemGroupSelections.includes(choiceIdx)) {
        updatedGroupSelections = [];
      } else {
        updatedGroupSelections = [choiceIdx];
      }

      return {
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          [groupIdx]: updatedGroupSelections,
        },
      };
    });

    setQuantities((prev) => {
      const currentItemQty = prev[itemId] || 0;
      if (currentItemQty === 0) {
        return { ...prev, [itemId]: 1 };
      }
      return prev;
    });
  };

  const handleToggleMultipleChoice = (itemId, groupIdx, choiceIdx) => {
    setSubChoiceQuantities((prev) => {
      const currentQty = prev[itemId]?.[groupIdx]?.[choiceIdx] || 0;
      const nextQty = currentQty > 0 ? 0 : 1;
      return {
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          [groupIdx]: {
            ...(prev[itemId]?.[groupIdx] || {}),
            [choiceIdx]: nextQty,
          },
        },
      };
    });

    setQuantities((prev) => {
      const currentItemQty = prev[itemId] || 0;
      if (currentItemQty === 0) {
        return { ...prev, [itemId]: 1 };
      }
      return prev;
    });
  };

  const handleStepMultipleChoice = (itemId, groupIdx, choiceIdx, delta) => {
    setSubChoiceQuantities((prev) => {
      const currentQty = prev[itemId]?.[groupIdx]?.[choiceIdx] || 0;
      const nextQty = Math.max(0, currentQty + delta);
      return {
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          [groupIdx]: {
            ...(prev[itemId]?.[groupIdx] || {}),
            [choiceIdx]: nextQty,
          },
        },
      };
    });

    setQuantities((prev) => {
      const currentItemQty = prev[itemId] || 0;
      if (currentItemQty === 0 && delta > 0) {
        return { ...prev, [itemId]: 1 };
      }
      return prev;
    });
  };

  let totalSum = 0;

  displayItems.forEach((item) => {
    let extraPrice = 0;
    if (item.subGroups && item.subGroups.length > 0) {
      item.subGroups.forEach((group, gIdx) => {
        if (group.selectionMode === 'multiple') {
          group.choices?.forEach((choice) => {
            const cQty = subChoiceQuantities[item.id]?.[gIdx]?.[choice.index] || 0;
            if (cQty > 0) {
              extraPrice += choice.price * cQty;
            }
          });
        } else {
          const selectedIndices = selectedChoices[item.id]?.[gIdx] || [];
          selectedIndices.forEach((cIdx) => {
            const choice = group.choices?.find((c) => c.index === cIdx);
            if (choice) {
              extraPrice += choice.price;
            }
          });
        }
      });
    }
    item.unitPrice = item.basePrice + extraPrice;
    const qty = quantities[item.id] || 0;
    totalSum += item.unitPrice * qty;
  });

  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(totalSum);
    }
  }, [totalSum, onTotalChange]);

  return {
    displayItems,
    quantities,
    selectedChoices,
    subChoiceQuantities,
    handleQuantityChange,
    handleSelectChoice,
    handleToggleMultipleChoice,
    handleStepMultipleChoice,
  };
}
